import { useQuery } from '@tanstack/react-query';
import { Search, SearchForm, VideosList, HourSelector, SponsorCTA } from './components';
import { getArena, getVideoHours, getVideosByHour } from '../../api';
import { isAdminUser } from '../../api/gestor';
import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../api/firebase';
import { MenuBar } from '../../components/ui/glow-menu';
import { DottedSurface } from '../../components/ui/dotted-surface';
import { Home, Video, Handshake, Radio, LogIn, ShieldCheck, Trophy, CircleDot, LayoutDashboard } from 'lucide-react';
import logoImg from '../../assets/images/logo.svg';
import { StickyFooter } from '../../components/ui/sticky-footer';
import { addRecentCourt } from '../../utils/recentCourts';

export const SearchContext = createContext<any>(undefined);

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// 'YYYY-MM-DD' -> '19 jul'
const formatDateLabel = (date: string): string | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!match) return undefined;
  const day = parseInt(match[3], 10);
  const month = MONTHS_PT[parseInt(match[2], 10) - 1];
  return month ? `${day} ${month}` : undefined;
};

const gestorMenuItem = {
  icon: LayoutDashboard, label: 'Gestor', href: '/gestor',
  gradient: 'radial-gradient(circle, rgba(126,226,63,0.2) 0%, rgba(126,226,63,0.06) 50%, transparent 100%)',
  iconColor: 'text-green-500',
};

const menuItems = [
  {
    icon: Home, label: 'Início', href: '/',
    gradient: 'radial-gradient(circle, rgba(245,138,37,0.2) 0%, rgba(229,118,5,0.08) 50%, transparent 100%)',
    iconColor: 'text-orange-500',
  },
  {
    icon: Video, label: 'Vídeos', href: '/videos',
    gradient: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, transparent 100%)',
    iconColor: 'text-blue-500',
  },
  {
    icon: CircleDot, label: 'FastTennis', href: '/fasttennis',
    gradient: 'radial-gradient(circle, rgba(43,76,255,0.25) 0%, rgba(43,76,255,0.08) 50%, transparent 100%)',
    iconColor: 'text-blue-400',
  },
  {
    icon: Radio, label: 'Ao Vivo', href: '/livestream',
    gradient: 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.06) 50%, transparent 100%)',
    iconColor: 'text-red-500',
  },
  {
    icon: ShieldCheck, label: 'FutSafe', href: '/futsafe',
    gradient: 'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 100%)',
    iconColor: 'text-yellow-500',
  },
  {
    icon: Trophy, label: 'Torneios', href: '/torneios',
    gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
  {
    icon: LogIn, label: 'Sair', href: '/login',
    gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
];

function Videos() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(auth.currentUser?.email ?? null);
  useEffect(() => onAuthStateChanged(auth, (u) => setUserEmail(u?.email ?? null)), []);
  const navItems = isAdminUser(userEmail) ? [...menuItems, gestorMenuItem] : menuItems;
  const [isFirstStep, setIsFirstStep] = useState(true);
  const [state, setState] = useState<{ id: string; value: string } | undefined>();
  const [city, setCity] = useState<any>();
  const [court, setCourt] = useState<any>();
  const [arena, setArena] = useState<any>();
  const [date, setDate] = useState('');
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [dataVideos, setDataVideos] = useState();
  const [shouldFetchHours, setShouldFetchHours] = useState(false);
  const [shouldFetchVideos, setShouldFetchVideos] = useState(false);

  const useHandleSearch = () => {
    // guarda a quadra buscada no navegador (atalho "Últimas quadras")
    if (state && city && arena && court) {
      addRecentCourt({ state, city, arena, court });
    }
    if (isFirstStep) setIsFirstStep(false);
    setSelectedHour(null);
    setDataVideos(undefined);
    setShouldFetchHours(true);
    refetchHours();
  };

  const { data: dataHours, refetch: refetchHours, isFetching: isFetchingHours } = useQuery({
    queryKey: ['getVideoHours', state?.id ?? '', city?.id ?? '', arena?.value ?? '', date, court?.value ?? ''],
    queryFn: getVideoHours,
    enabled: shouldFetchHours && !!date && !!arena && !!court,
  });

  const { data: dataVideosByHour, refetch: refetchVideos, isFetching: isFetchingVideos } = useQuery({
    queryKey: ['getVideosByHour', state?.id ?? '', city?.id ?? '', arena?.value ?? '', date, court?.value ?? '', selectedHour],
    queryFn: getVideosByHour,
    enabled: shouldFetchVideos && !!date && !!arena && !!court && selectedHour !== null,
  });

  const handleSelectHour = (hour: number) => {
    setSelectedHour(hour);
    setShouldFetchVideos(true);
  };

  useEffect(() => {
    if (shouldFetchHours && dataHours) setShouldFetchHours(false);
  }, [dataHours, shouldFetchHours]);

  useEffect(() => {
    if (dataVideosByHour) {
      setDataVideos(dataVideosByHour);
      setShouldFetchVideos(false);
    }
  }, [dataVideosByHour]);

  useEffect(() => {
    if (selectedHour !== null && shouldFetchVideos) refetchVideos();
  }, [selectedHour, shouldFetchVideos, refetchVideos]);

  const { data: dataArena } = useQuery({
    queryKey: ['getArena', city?.value ?? 'Belo Horizonte'],
    queryFn: getArena,
  });

  const handleClearSearch = () => {
    setIsFirstStep(true);
    setSelectedHour(null);
    setDataVideos(undefined);
    setShouldFetchHours(false);
    setShouldFetchVideos(false);
  };

  const handleMenuClick = (label: string) => {
    if (label === 'Sair') {
      signOut(auth).finally(() => {
        localStorage.removeItem('accessToken');
        navigate('/login');
      });
      return;
    }
    if (label === 'Início') {
      navigate('/');
      return;
    }
    const item = navItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  return (
    <SearchContext.Provider
      value={{ city, setCity, state, setState, court, setCourt, arena, setArena, date, setDate, dataArena }}
    >
      <div className="flex flex-col min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background */}
        <DottedSurface />

        {/* Navbar */}
        <header
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5"
          style={{ backgroundColor: 'rgba(10,10,10,0.8)' }}
        >
          <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
          <MenuBar items={navItems} activeItem="Vídeos" onItemClick={handleMenuClick} />
        </header>

        {/* Content */}
        <div className="relative z-10 flex-1">
          {isFirstStep ? (
            <SearchForm onSearch={useHandleSearch} dataArena={dataArena} />
          ) : (
            <>
              <Search dataArena={dataArena} onSearch={useHandleSearch} onClear={handleClearSearch} />
              <SponsorCTA arena={arena} city={city} />
              <HourSelector
                hours={dataHours || []}
                selectedHour={selectedHour}
                onSelectHour={handleSelectHour}
                isLoading={isFetchingHours}
              />
              {selectedHour !== null && (
                isFetchingVideos ? (
                  <div className="text-center py-12" style={{ backgroundColor: '#0a0a0a' }}>
                    <p className="text-zinc-500">Carregando vídeos...</p>
                  </div>
                ) : (
                  <VideosList
                    dataVideos={dataVideos}
                    courtLabel={[arena?.value, court?.value].filter(Boolean).join(' · ') || undefined}
                    dateLabel={formatDateLabel(date)}
                  />
                )
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <StickyFooter />
      </div>
    </SearchContext.Provider>
  );
}

export default Videos;
